<template>
  <div class="notifications-page">
    <div class="page-header">
      <div class="title-stack">
        <div>
          <h1>Notifica&ccedil;&otilde;es</h1>
        </div>
        <span class="badge" :class="{ muted: unreadCount === 0 }">
          {{ unreadCount }}
        </span>
      </div>
      <div class="header-actions">
        <div class="filter-group">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            class="filter-button"
            :class="{ active: activeFilter === option.value }"
            @click="setFilter(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <button class="chip" @click="markAllAsRead" :disabled="unreadCount === 0">
          Marcar todas como lidas
        </button>
      </div>
    </div>

    <ul class="notifications-list" v-if="filteredNotifications.length">
      <li
        v-for="notif in filteredNotifications"
        :key="notif.id || `${notif.eventKey || 'n'}-${notif.variant || 'v'}-${notif.createdAt || notif.title || 'x'}`"
        class="notification-item"
        :class="{ unread: notif.unread }"
      >
        <div class="notif-main">
          <span
            class="dot"
            :class="[{ unread: notif.unread }, notif.type, notif.variant]"
            aria-hidden="true"
          ></span>
          <div class="text-block">
            <div class="title-row">
              <h2 class="notif-title">{{ notif.title }}</h2>
            </div>
            <p class="notif-body">{{ notif.description }}</p>
          </div>
        </div>
        <div class="notif-actions">
          <button
            v-if="isWelcomeNotification(notif)"
            type="button"
            class="welcome-reopen-btn"
            @click.stop="openWelcomeModalFromNotification"
            aria-label="Reabrir boas-vindas"
          >
            <i class="fas fa-star" aria-hidden="true"></i>
          </button>
          <span class="type-chip" :class="[notif.type, notif.variant]">{{ notif.typeLabel }}</span>
          <button
            v-if="notif.unread"
            class="ghost mark-read"
            @click="markAsRead(notif)"
            aria-label="Marcar como lida"
          >
            Marcar lida
          </button>
          <button class="ghost danger" @click="deleteNotification(notif)" aria-label="Excluir notifica&ccedil;&atilde;o">
            Excluir
          </button>
        </div>
      </li>
    </ul>

    <div v-else class="empty-state">
      <div class="empty-icon" aria-hidden="true">
        <i class="fas fa-bell-slash"></i>
      </div>
      <p>Nenhuma notifica&ccedil;&atilde;o por aqui!</p>
    </div>
    <WelcomeFirstAccessModal
      :open="showWelcomeModal"
      :username="username"
      @close="closeWelcomeModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { API_BASE_URL } from '@/config/env';
import WelcomeFirstAccessModal from './WelcomeFirstAccessModal.vue';

const NOTIF_STORAGE_KEY = 'notifications-data';
const WELCOME_NOTIF_EVENT_PREFIX = 'system-welcome';

const normalizeNotificationText = (value) => {
  const text = String(value ?? '');
  return text
    .replace(/\bInsignia\b/g, 'Insígnia')
    .replace(/\binsignia\b/g, 'insígnia')
    .replace(/\bVoce\b/g, 'Você')
    .replace(/\bvoce\b/g, 'você')
    .replace(/\bapos\b/g, 'após')
    .replace(/\batras\b/g, 'atrás');
};

const normalizeNotification = (notif) => {
  if (!notif || typeof notif !== 'object') return notif;

  const normalizedType = notif.type === 'alert' ? 'trade' : notif.type;
  let normalizedVariant = notif.variant;
  if (!normalizedVariant && normalizedType === 'trade') {
    normalizedVariant = 'trade-open';
  }
  if (notif.type === 'alert') {
    normalizedVariant = 'trade-stop';
  }

  return {
    ...notif,
    id: notif.id,
    eventKey: notif.eventKey || notif.event_key || '',
    meta: notif.meta && typeof notif.meta === 'object' ? notif.meta : {},
    type: normalizedType,
    typeLabel: normalizedType === 'trade' ? 'Trade' : normalizeNotificationText(notif.typeLabel || notif.type_label),
    variant: normalizedVariant,
    title: normalizeNotificationText(notif.title),
    description: normalizeNotificationText(notif.description),
    time: normalizeNotificationText(notif.time || ''),
    createdAt: notif.createdAt || notif.created_at || null,
    unread: typeof notif.unread === 'boolean' ? notif.unread : true,
  };
};

const loadNotifications = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeNotification);
  } catch (e) {
    return [];
  }
};

const saveNotifications = (list) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore storage errors
  }
};

const emitNotificationsUpdated = (list) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notifications-updated', { detail: list }));
};

const ensureWelcomeNotification = (list) => {
  return Array.isArray(list) ? list : [];
};

const formatRelativeTime = (isoValue) => {
  const date = new Date(isoValue);
  if (!isoValue || Number.isNaN(date.getTime())) return 'agora';

  const diffMs = Date.now() - date.getTime();
  if (diffMs <= 0) return 'agora';

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'agora';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)} min atrás`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h atrás`;
  return `${Math.floor(diffMs / day)}d atrás`;
};

export default {
  name: 'Notifications',
  components: {
    WelcomeFirstAccessModal,
  },
  data() {
    return {
      activeFilter: 'all',
      filterOptions: [
        { label: 'Todas', value: 'all' },
        { label: 'Sistema', value: 'system' },
        { label: 'Trade', value: 'trade' },
        { label: 'Conta', value: 'account' },
      ],
      notifications: ensureWelcomeNotification(loadNotifications()),
      showWelcomeModal: false,
    };
  },
  computed: {
    username() {
      return this.$store?.getters?.username || 'Trader';
    },
    unreadCount() {
      return this.notifications.filter(n => n.unread).length;
    },
    filteredNotifications() {
      let list = this.notifications;
      if (this.activeFilter !== 'all') {
        list = list.filter(n => n.type === this.activeFilter);
      }
      return list;
    },
  },
  mounted() {
    const seeded = ensureWelcomeNotification(this.notifications);
    if (seeded.length !== this.notifications.length) {
      this.notifications = seeded;
      saveNotifications(seeded);
      emitNotificationsUpdated(seeded);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this._onStorageUpdate);
    }
    this.fetchNotificationsFromApi();
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this._onStorageUpdate);
    }
  },
  methods: {
    isWelcomeNotification(notif) {
      return !!(notif?.eventKey && String(notif.eventKey).startsWith(WELCOME_NOTIF_EVENT_PREFIX));
    },
    openWelcomeModalFromNotification() {
      this.showWelcomeModal = true;
    },
    closeWelcomeModal() {
      this.showWelcomeModal = false;
    },
    setFilter(filter) {
      this.activeFilter = filter;
    },
    async markAllAsRead() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        await axios.patch(`${API_BASE_URL}/api/v1/notifications/read-all`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.notifications = this.notifications.map(item => ({ ...item, unread: false }));
        saveNotifications(this.notifications);
        emitNotificationsUpdated(this.notifications);
      } catch (e) {
        // Keep current UI if request fails.
      }
    },
    async markAsRead(notif) {
      if (!notif) return;

      if (!notif.id) {
        this.notifications = this.notifications.map(item =>
          item.eventKey === notif.eventKey ? { ...item, unread: false } : item
        );
        saveNotifications(this.notifications);
        emitNotificationsUpdated(this.notifications);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        await axios.patch(`${API_BASE_URL}/api/v1/notifications/${notif.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.notifications = this.notifications.map(item =>
          item.id === notif.id ? { ...item, unread: false } : item
        );
        saveNotifications(this.notifications);
        emitNotificationsUpdated(this.notifications);
      } catch (e) {
        // Keep current UI if request fails.
      }
    },
    async deleteNotification(notif) {
      if (!notif) return;

      if (!notif.id) {
        this.notifications = this.notifications.filter(item => item.eventKey !== notif.eventKey);
        saveNotifications(this.notifications);
        emitNotificationsUpdated(this.notifications);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        await axios.delete(`${API_BASE_URL}/api/v1/notifications/${notif.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.notifications = this.notifications.filter(item => item.id !== notif.id);
        saveNotifications(this.notifications);
        emitNotificationsUpdated(this.notifications);
      } catch (e) {
        // Keep current UI if request fails.
      }
    },
    mapApiNotification(item) {
      const normalized = normalizeNotification({
        id: item.id,
        event_key: item.event_key,
        type: item.type,
        type_label: item.type_label,
        variant: item.variant,
        meta: item.meta,
        title: item.title,
        description: item.description,
        unread: item.unread,
        created_at: item.created_at,
      });

      return {
        ...normalized,
        time: formatRelativeTime(item.created_at),
      };
    },
    async fetchNotificationsFromApi() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/notifications`, {
          params: { limit: 120 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const rows = Array.isArray(response?.data?.notifications) ? response.data.notifications : [];
        const mapped = ensureWelcomeNotification(rows.map(this.mapApiNotification));
        this.notifications = mapped;
        saveNotifications(mapped);
        emitNotificationsUpdated(mapped);
      } catch (e) {
        // Keep local cache if API is unavailable.
      }
    },
    _onStorageUpdate(e) {
      if (e.key === NOTIF_STORAGE_KEY) {
        this.notifications = ensureWelcomeNotification(loadNotifications());
      }
    },
  },
};
</script>

<style scoped>
:root {
  --card-bg: #1c2029;
  --card-border: rgba(255, 255, 255, 0.06);
  --text-strong: #eef2ff;
  --text-body: #c8d0e0;
  --text-muted: #8f9bb3;
  --primary: #4e73df;
  --primary-strong: #5b8bff;
  --danger: #f25f5c;
}

.notifications-page {
  max-width: 960px;
  width: 100%;
  margin: calc(var(--app-header-h) + 18px) auto 48px;
  padding: 0 var(--app-gutter) 48px;
  color: var(--text-strong);
  font-family: 'Montserrat', sans-serif;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  overflow: hidden;
}

.notifications-page::before {
  content: '';
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at top left, rgba(78, 115, 223, 0.12), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.14), transparent 40%),
    #11131a;
  z-index: 0;
  pointer-events: none;
}

.notifications-page::after {
  content: '';
  position: fixed;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
  z-index: 0;
  pointer-events: none;
}

.notifications-page > * {
  position: relative;
  z-index: 1;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.title-stack {
  display: flex;
  align-items: center;
  gap: 12px;
}

h1 {
  font-size: 1.75rem;
  margin: 0 0 4px;
}

.subtitle {
  margin: 0;
  color: rgba(200, 208, 224, 0.7);
  font-size: 0.95rem;
}

.badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  background: rgba(78, 115, 223, 0.18);
  color: var(--text-strong);
  border: 1px solid rgba(78, 115, 223, 0.35);
  box-shadow: 0 6px 16px rgba(78, 115, 223, 0.18);
}

.badge.muted {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  box-shadow: none;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-group {
  display: inline-flex;
  gap: 10px;
  padding: 5px;
  border-radius: 999px;
  background: rgba(20, 26, 38, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
  backdrop-filter: blur(8px);
}

.filter-button {
  min-width: 90px;
  padding: 9px 14px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(226, 232, 255, 0.75);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-button:hover,
.filter-button.active {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  color: #fff;
  box-shadow: 0 0 14px rgba(115, 148, 240, 0.4);
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  color: var(--text-body);
  cursor: pointer;
}

.toggle input {
  accent-color: var(--primary);
  cursor: pointer;
}

.chip {
  background: linear-gradient(135deg, #4e73df 0%, #7f6df1 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 9px 14px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.2s ease;
  box-shadow: 0 10px 24px rgba(78, 115, 223, 0.25);
}

.chip:hover:enabled {
  transform: translateY(-1px);
  opacity: 0.95;
}

.chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notifications-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notification-item {
  background: linear-gradient(145deg, rgba(28, 32, 41, 0.9), rgba(22, 26, 34, 0.92));
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px 14px;
  align-items: center;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 0.2s ease;
  position: relative;
  overflow: hidden;
}

.notification-item:hover {
  border-color: rgba(78, 115, 223, 0.35);
}

.notification-item.unread {
  border-color: rgba(115, 148, 240, 0.72);
  background: linear-gradient(135deg, #27345a 0%, #322d58 100%);
  box-shadow: 0 0 0 1px rgba(115, 148, 240, 0.24), 0 12px 28px rgba(15, 20, 35, 0.35);
}

.notif-main {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  margin-top: 0;
  align-self: center;
  display: inline-block;
  position: relative;
  background: #8f9bb3;
  color: #8f9bb3; /* used by ::after */
  box-shadow: 0 0 8px currentColor;
}

.dot::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  transform: translate(-50%, -50%) scale(1);
  background: currentColor;
  opacity: 0.6;
  filter: blur(0.5px);
  animation: status-ping 1.6s ease-out infinite;
}

.dot.trade {
  background: #60a5fa;
  color: #60a5fa;
}

.dot.trade-open {
  background: #60a5fa;
  color: #60a5fa;
}

.dot.trade-win {
  background: #34d399;
  color: #34d399;
}

.dot.trade-stop {
  background: #f25f5c;
  color: #f25f5c;
}

.dot.system {
  background: #e5e7eb;
  color: #e5e7eb;
}

.dot.account {
  background: #ffd166;
  color: #ffd166;
}


.text-block {
  flex: 1;
  min-width: 0;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.welcome-reopen-btn {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(138, 168, 255, 0.42);
  background:
    linear-gradient(165deg, rgba(18, 25, 43, 0.74), rgba(12, 17, 30, 0.78)) padding-box,
    linear-gradient(135deg, rgba(78, 115, 223, 0.72), rgba(155, 89, 182, 0.68)) border-box;
  color: #dfe7ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
  position: relative;
  top: 1px;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.welcome-reopen-btn i {
  font-size: 0.72rem;
}

.welcome-reopen-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(180, 200, 255, 0.75);
  box-shadow: 0 6px 14px rgba(15, 20, 35, 0.35);
}

.type-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-strong);
}

.type-chip.trade {
  border-color: rgba(126, 207, 255, 0.35);
  background: rgba(126, 207, 255, 0.12);
  color: #c8ecff;
}

.type-chip.trade-open {
  border-color: rgba(126, 207, 255, 0.35);
  background: rgba(126, 207, 255, 0.12);
  color: #c8ecff;
}

.type-chip.trade-win {
  border-color: rgba(123, 255, 186, 0.38);
  background: rgba(123, 255, 186, 0.14);
  color: #d9ffe9;
}

.type-chip.trade-stop {
  border-color: rgba(242, 95, 92, 0.35);
  background: rgba(242, 95, 92, 0.14);
  color: #ffdede;
}

.type-chip.account {
  border-color: rgba(255, 209, 102, 0.4);
  background: rgba(255, 209, 102, 0.14);
  color: #ffd166;
}

.type-chip.system {
  border-color: rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.08);
}


.notif-title {
  margin: 0;
  font-size: 1.02rem;
  color: var(--text-strong);
}

.notif-time {
  font-size: 0.9rem;
  color: var(--text-muted);
  white-space: nowrap;
}

.notif-body {
  margin: 6px 0 0;
  font-size: 0.93rem;
  color: var(--text-body);
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.notif-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
}

.ghost {
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 7px 12px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
}

.notification-item.unread .ghost {
  background: rgba(78, 115, 223, 0.12);
  border-color: rgba(115, 148, 240, 0.4);
  color: #e5eaff;
}

.notification-item.unread .ghost:hover {
  background: rgba(115, 148, 240, 0.2);
  border-color: rgba(115, 148, 240, 0.55);
}

.ghost:hover {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}

.ghost.danger {
  border-color: rgba(242, 95, 92, 0.32);
  color: #ffdede;
  background: rgba(242, 95, 92, 0.08);
}

.ghost.danger:hover {
  background: rgba(242, 95, 92, 0.16);
}

.ghost.mark-read {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.92), rgba(127, 109, 241, 0.92));
  border-color: rgba(127, 109, 241, 0.52);
  color: #f3f6ff;
  box-shadow: none;
}

.ghost.mark-read:hover {
  background: linear-gradient(120deg, rgba(78, 115, 223, 1), rgba(127, 109, 241, 1));
  border-color: rgba(127, 109, 241, 0.68);
}

.notification-item.unread .ghost.danger {
  border-color: rgba(242, 95, 92, 0.38);
  color: #ffdede;
  background: rgba(242, 95, 92, 0.14);
}

.notification-item.unread .ghost.danger:hover {
  background: rgba(242, 95, 92, 0.22);
  border-color: rgba(242, 95, 92, 0.5);
}

/* Gamified pulse for nao lidas */
@keyframes status-ping {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  60% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
}

.empty-state {
  margin-top: 60px;
  text-align: center;
  color: var(--text-body);
  display: grid;
  gap: 8px;
  justify-items: center;
}

.empty-icon {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  font-size: 1.4rem;
}

.muted-text {
  color: var(--text-muted);
}

@media (max-width: 767px) {
  .notifications-page {
    margin-top: calc(var(--app-header-h) + 12px);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .title-stack {
    width: 100%;
    justify-content: space-between;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .filter-group {
    width: 100%;
    overflow-x: auto;
  }

  .notification-item {
    grid-template-columns: 1fr;
  }

  .notif-actions {
    justify-content: flex-start;
  }
}
</style>


